import { HeroImageScene } from './floema-floating-hero/hero-image-scene.js';
import {
  getFloatingHeroProfile,
  shouldInitFloemaFloatingHero,
} from './floema-floating-hero/hero-profile.js';
import {
  DEFAULT_PREWARM_TIMEOUT,
  floemaHeroPrewarm,
} from './floema-floating-hero/hero-prewarm.js';
import { getScenePlayDelay } from './floema-floating-hero/scene-timing.js';

const TITLE_ANIMATION_FALLBACK = 3200;
const TITLE_ANIMATION_BUFFER = 180;
const TITLE_WORD_DURATION = 1000;
const TITLE_WORD_DELAY_BASE = 300;
const TITLE_WORD_DELAY_STEP = 300;

let activeHeroScene = null;

const getNetworkConnection = () =>
  typeof navigator === 'undefined'
    ? {}
    : navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection ||
      {};

const getFirstSrcsetUrl = (srcset = '') =>
  srcset.split(',')[0]?.trim().split(/\s+/)[0] || '';

const readSource = (card, index) => {
  const img = card.querySelector('img');
  const source = card.querySelector('source');
  const src = img?.dataset.src || img?.getAttribute('src') || '';
  const srcset = source?.dataset.srcset || source?.getAttribute('srcset') || '';
  const webp = getFirstSrcsetUrl(srcset);

  return {
    id: `floema-source-${index}`,
    alt: img?.getAttribute('alt') || '',
    src,
    fallbackSrc: src,
    webp,
    srcset,
  };
};

const parseTimeList = (value) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) =>
      item.endsWith('ms') ? parseFloat(item) : parseFloat(item) * 1000
    )
    .filter((item) => Number.isFinite(item));

const getTitleWordIndex = (word, fallback) => {
  const styles = window.getComputedStyle(word);
  const index = parseFloat(styles.getPropertyValue('--word-index'));

  return Number.isFinite(index) ? index : fallback;
};

const getEstimatedTitleAnimationTime = (words) => {
  if (!words.length) return TITLE_ANIMATION_FALLBACK;

  const maxWordIndex = words.reduce(
    (maxIndex, word, index) =>
      Math.max(maxIndex, getTitleWordIndex(word, index)),
    0
  );

  return (
    TITLE_WORD_DURATION +
    TITLE_WORD_DELAY_BASE +
    TITLE_WORD_DELAY_STEP * maxWordIndex
  );
};

const getTitleAnimationDelay = (titleEl) => {
  const words = Array.from(titleEl.querySelectorAll('.word'));
  if (!words.length) return TITLE_ANIMATION_FALLBACK;

  const maxWordTime = words.reduce((maxTime, word) => {
    const styles = window.getComputedStyle(word);
    const durations = parseTimeList(styles.animationDuration);
    const delays = parseTimeList(styles.animationDelay);
    const duration = durations[0] || 0;
    const delay = delays[0] || 0;

    return Math.max(maxTime, duration + delay);
  }, 0);

  return (
    Math.max(maxWordTime, getEstimatedTitleAnimationTime(words)) +
    TITLE_ANIMATION_BUFFER
  );
};

const schedule = (callback, delaySeconds) => {
  if (typeof gsap !== 'undefined') {
    gsap.delayedCall(delaySeconds, callback);
    return;
  }

  setTimeout(callback, delaySeconds * 1000);
};

const hasWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(
      canvas.getContext('webgl2') || canvas.getContext('webgl')
    );
  } catch {
    return false;
  }
};

const createCanvas = (scene) => {
  const canvas = document.createElement('canvas');
  canvas.className = 'first-sec__floema-floating-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  scene.innerHTML = '';
  scene.appendChild(canvas);
  return canvas;
};

const getHeroRuntimeContext = (options = {}) => {
  if (!shouldInitFloemaFloatingHero(window.innerWidth)) return null;
  if (!hasWebGLSupport()) return null;

  const root = document.querySelector('[data-floema-floating-hero]');
  if (!root || (options.skipInited && root.dataset.inited === '1')) return null;

  const scene = root.querySelector('[data-floema-floating-scene]');
  const sourceCards = Array.from(
    root.querySelectorAll('[data-floema-floating-card]')
  );

  if (!scene || !sourceCards.length) return null;

  const sources = sourceCards.map(readSource).filter((source) => source.src);
  if (!sources.length) return null;

  return {
    root,
    scene,
    sources,
    profile: getFloatingHeroProfile(window.innerWidth, getNetworkConnection()),
  };
};

const initScrollFade = (root) => {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    return;
  }

  gsap.fromTo(
    root,
    { opacity: 1 },
    {
      opacity: 0.5,
      ease: 'none',
      scrollTrigger: {
        trigger: '.first-sec.home-first-sec',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    }
  );
};

const playScene = (root, heroScene) => {
  if (!heroScene || heroScene.isDestroyed) return;

  root.classList.add('is-ready');
  heroScene.play();
  initScrollFade(root);
};

const watchTitleAndPlay = (root, heroScene) => {
  const titleEl = document.querySelector('.home-first-sec .first-sec__title');

  const playAfterTitleAnimation = () => {
    schedule(
      () => playScene(root, heroScene),
      getScenePlayDelay(getTitleAnimationDelay(titleEl)) / 1000
    );
  };

  if (!titleEl) {
    setTimeout(() => playScene(root, heroScene), getScenePlayDelay(3000));
    return;
  }

  if (titleEl.classList.contains('aos-animate')) {
    playAfterTitleAnimation();
    return;
  }

  let started = false;

  const tryStart = () => {
    if (started) return;
    if (titleEl.classList.contains('aos-animate')) {
      started = true;
      observer.disconnect();
      clearTimeout(fallback);
      playAfterTitleAnimation();
    }
  };

  const observer = new MutationObserver(tryStart);
  observer.observe(titleEl, {
    attributes: true,
    attributeFilter: ['class'],
  });

  const fallback = setTimeout(() => {
    if (started) return;
    started = true;
    observer.disconnect();
    playScene(root, heroScene);
  }, getScenePlayDelay(5500));
};

window.floemaFloatingHeroPrewarm = function floemaFloatingHeroPrewarm(options = {}) {
  try {
    const context = getHeroRuntimeContext({ skipInited: true });

    if (!context) {
      return Promise.resolve(false);
    }

    return floemaHeroPrewarm.prewarm(context.sources, context.profile, {
      timeout: options.timeout ?? DEFAULT_PREWARM_TIMEOUT,
    });
  } catch {
    return Promise.resolve(false);
  }
};

window.floemaFloatingHeroAnimation = function floemaFloatingHeroAnimation() {
  const context = getHeroRuntimeContext({ skipInited: true });
  if (!context) return;

  const { root, scene, sources, profile } = context;
  const canvas = createCanvas(scene);
  const prewarmedTextureCache = floemaHeroPrewarm.consume(sources, profile);

  root.dataset.inited = '1';

  let nextHeroScene = null;

  try {
    activeHeroScene?.destroy();
    nextHeroScene = new HeroImageScene(root, sources, {
      ...profile,
      canvas,
      gsap: typeof gsap === 'undefined' ? null : gsap,
      textureCache: prewarmedTextureCache ?? undefined,
      ownsTextureCache: prewarmedTextureCache ? true : undefined,
    });
    activeHeroScene = nextHeroScene.start();
  } catch {
    if (nextHeroScene) {
      nextHeroScene.destroy();
    } else {
      prewarmedTextureCache?.dispose?.();
    }
    activeHeroScene = null;
    root.classList.remove('is-ready');
    delete root.dataset.inited;
    scene.innerHTML = '';
    return;
  }

  if (!activeHeroScene?.isStarted) {
    delete root.dataset.inited;
    scene.innerHTML = '';
    return;
  }

  watchTitleAndPlay(root, activeHeroScene);
};

const scheduleFloemaFloatingHeroAutoInit = () => {
  if (!document.querySelector('[data-floema-floating-hero]')) return;
  if (!shouldInitFloemaFloatingHero(window.innerWidth)) return;

  const init = () => {
    const delay = document.querySelector('.bold-loader-new') ? 3200 : 0;
    setTimeout(() => {
      window.floemaFloatingHeroAnimation();
    }, delay);
  };

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init, { once: true });
  }
};

window.addEventListener('beforeunload', () => {
  activeHeroScene?.destroy();
});

scheduleFloemaFloatingHeroAutoInit();
